import { Box } from '@mui/material';

import { SHRED_PRIZE_IDS } from '../constants';

const ART_VIEWBOX = '0 0 100 100';

const ICONS = {
  shredChampion: (fill) => (
    <>
      <path d="M30 18h40v24c0 15-9 25-20 25S30 57 30 42z" fill={fill} />
      <path d="M30 24H17c0 15 7 23 17 25M70 24h13c0 15-7 23-17 25" fill="none" stroke={fill} strokeWidth="6" strokeLinecap="round" />
      <rect x="45" y="66" width="10" height="13" fill={fill} />
      <rect x="32" y="79" width="36" height="9" rx="3" fill={fill} />
      <path d="M50 26l4 9h9l-7 6 3 9-9-5-9 5 3-9-7-6h9z" fill="#fff" opacity="0.85" />
      <path d="M36 22v20c0 6 2 11 5 15" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity="0.5" />
    </>
  ),
  underdog: (fill) => (
    <>
      <ellipse cx="50" cy="66" rx="20" ry="16" fill={fill} />
      <ellipse cx="25" cy="46" rx="8" ry="11" transform="rotate(-20 25 46)" fill={fill} />
      <ellipse cx="41" cy="31" rx="8" ry="12" transform="rotate(-6 41 31)" fill={fill} />
      <ellipse cx="59" cy="31" rx="8" ry="12" transform="rotate(6 59 31)" fill={fill} />
      <ellipse cx="75" cy="46" rx="8" ry="11" transform="rotate(20 75 46)" fill={fill} />
      <ellipse cx="44" cy="60" rx="6" ry="4" fill="#fff" opacity="0.35" />
    </>
  ),
  paceKing: (fill) => (
    <>
      <path d="M58 6L22 56h24l-8 38 40-54H54z" fill={fill} />
      <path d="M58 6L38 36h12z" fill="#fff" opacity="0.5" />
    </>
  ),
  ironEngine: (fill) => (
    <>
      {[0, 45, 90, 135].map((angle) => (
        <rect key={angle} x="42" y="8" width="16" height="84" rx="4" transform={`rotate(${angle} 50 50)`} fill={fill} />
      ))}
      <circle cx="50" cy="50" r="28" fill={fill} />
      <circle cx="50" cy="50" r="11" fill="#0c0908" />
      <circle cx="50" cy="50" r="28" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.35" />
    </>
  ),
  ironHabit: (fill) => (
    <>
      <path d="M50 6c9 17 30 27 24 54-3 15-14 26-24 26S29 75 26 60c-1-10 5-17 10-26 4 9 9 9 9-1 0-9-1-16 5-27z" fill={fill} />
      <path d="M50 44c5 9 14 14 12 27-1 8-6 13-12 13s-11-5-12-13c-1-8 4-12 7-17 3 6 5 5 5-1z" fill="#fff" opacity="0.7" />
    </>
  ),
  rookieStar: (fill) => (
    <>
      <path d="M50 6l13 29 31 3-23 21 7 31-28-16-28 16 7-31L6 38l31-3z" fill={fill} />
      <path d="M50 20l7 17 17 2-13 11 4 17-15-9z" fill="#fff" opacity="0.45" />
    </>
  ),
  [SHRED_PRIZE_IDS.CREW]: (fill) => (
    <>
      <rect x="18" y="42" width="64" height="42" rx="5" fill={fill} />
      <rect x="13" y="32" width="74" height="16" rx="5" fill={fill} />
      <rect x="44" y="32" width="12" height="52" fill="#fff" opacity="0.85" />
      <path d="M50 32C38 14 20 18 26 28c3 5 14 4 24 4zM50 32c12-18 30-14 24-4-3 5-14 4-24 4z" fill="none" stroke="#fff" strokeWidth="5" strokeLinejoin="round" opacity="0.85" />
    </>
  ),
  [SHRED_PRIZE_IDS.PODIUM]: (fill) => (
    <>
      <path d="M32 6h14l8 26H40zM68 6H54l-8 26h14z" fill={fill} opacity="0.7" />
      <circle cx="50" cy="62" r="26" fill={fill} />
      <circle cx="50" cy="62" r="19" fill="none" stroke="#fff" strokeWidth="3" opacity="0.5" />
      <path d="M50 48l4 9 10 1-8 7 3 10-9-6-9 6 3-10-8-7 10-1z" fill="#fff" opacity="0.85" />
    </>
  ),
};

export const hasPrizeArt = (prizeId) => Object.hasOwn(ICONS, prizeId);

const PrizeArt = ({ prizeId, accent, size }) => {
  if (!hasPrizeArt(prizeId)) {
    return null;
  }
  const gradientId = `shred-art-${prizeId}`;
  return (
    <Box
      component="svg"
      aria-hidden="true"
      viewBox={ART_VIEWBOX}
      sx={{ width: `${size}px`, height: `${size}px`, overflow: 'visible', filter: `drop-shadow(0 8px 18px ${accent}cc)` }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff3c4" />
          <stop offset="45%" stopColor={accent} />
          <stop offset="100%" stopColor={accent} stopOpacity="0.75" />
        </linearGradient>
      </defs>
      {ICONS[prizeId](`url(#${gradientId})`)}
    </Box>
  );
};

export default PrizeArt;

export const PrizeIcon = ({ prize, size }) =>
  hasPrizeArt(prize.id) ? (
    <PrizeArt prizeId={prize.id} accent={prize.accent} size={size} />
  ) : (
    <Box aria-hidden="true" sx={{ fontSize: `${size}px`, lineHeight: 1 }}>{prize.icon}</Box>
  );

const ITEM_ICONS = {
  meals: () => (
    <>
      <path d="M12 42h76l-6 40a8 8 0 01-8 6H26a8 8 0 01-8-6z" fill="#f4efe6" />
      <path d="M50 42v46" stroke="#5b4636" strokeWidth="3" />
      <circle cx="31" cy="60" r="10" fill="#e8472b" />
      <path d="M26 52c3-4 8-4 10 0" fill="none" stroke="#2f9e6e" strokeWidth="4" strokeLinecap="round" />
      <path d="M58 70c0-8 8-14 16-8 6 4 6 12 0 16H62c-3-2-4-5-4-8z" fill="#7fc241" />
      <ellipse cx="70" cy="58" rx="9" ry="5" fill="#f0a63a" />
      <rect x="8" y="32" width="84" height="12" rx="5" fill="#2f9e6e" />
      <path d="M36 26c-3-5 3-8 0-13M52 26c-3-5 3-8 0-13M68 26c-3-5 3-8 0-13" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity="0.7" />
    </>
  ),
  giftCard: () => (
    <>
      <rect x="6" y="22" width="88" height="56" rx="9" fill="#2f9e6e" />
      <rect x="6" y="22" width="88" height="56" rx="9" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.5" />
      <rect x="6" y="34" width="88" height="10" fill="#0c0908" opacity="0.35" />
      <text x="50" y="68" textAnchor="middle" fontSize="26" fontWeight="800" fill="#fff" fontFamily="Barlow Condensed, sans-serif">$100</text>
    </>
  ),
  gear: () => (
    <>
      <circle cx="40" cy="58" r="30" fill="#f0b73a" />
      <circle cx="40" cy="58" r="22" fill="none" stroke="#a8741a" strokeWidth="3" />
      <text x="40" y="68" textAnchor="middle" fontSize="28" fontWeight="800" fill="#a8741a" fontFamily="Barlow Condensed, sans-serif">G</text>
      <circle cx="68" cy="38" r="24" fill="#f7d060" />
      <circle cx="68" cy="38" r="17" fill="none" stroke="#a8741a" strokeWidth="3" />
      <text x="68" y="47" textAnchor="middle" fontSize="24" fontWeight="800" fill="#a8741a" fontFamily="Barlow Condensed, sans-serif">G</text>
    </>
  ),
  helmet: () => (
    <>
      <path d="M12 58C12 30 30 14 52 14s36 16 36 40v20H44c-8 0-14-6-14-14H12z" fill="#6aa7ff" />
      <path d="M44 74h44V54H44z" fill="#2c4a7a" opacity="0.5" />
      <rect x="38" y="36" width="50" height="20" rx="8" fill="#0f1b2e" />
      <path d="M62 20l-12 22h9l-5 16 18-24h-10l8-14z" fill="#ffe14d" />
    </>
  ),
  dumbbell: () => (
    <>
      <rect x="30" y="44" width="40" height="12" rx="4" fill="#b8bcc6" />
      <rect x="14" y="28" width="14" height="44" rx="5" fill="#6b7280" />
      <rect x="72" y="28" width="14" height="44" rx="5" fill="#6b7280" />
      <rect x="4" y="36" width="10" height="28" rx="4" fill="#4b5563" />
      <rect x="86" y="36" width="10" height="28" rx="4" fill="#4b5563" />
    </>
  ),
  ticket: () => (
    <>
      <path d="M8 26h84v16a8 8 0 000 16v16H8V58a8 8 0 000-16z" fill="#ff7a3c" />
      <path d="M66 26v52" stroke="#fff" strokeWidth="3" strokeDasharray="5 5" />
      <path d="M36 36c8 12 14 14 12 26-1 6-5 10-10 10s-9-4-10-10c0-6 4-9 6-13 2 4 4 3 4-1z" fill="#ffe14d" />
      <path d="M78 38l3 7 7 1-5 5 1 7-6-4-6 4 1-7-5-5 7-1z" fill="#fff" />
    </>
  ),
  title: () => (
    <>
      <rect x="6" y="30" width="88" height="40" rx="8" fill="#8b5cf6" />
      <rect x="12" y="36" width="76" height="28" rx="5" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.7" />
      <path d="M50 40l3 7 8 1-6 5 2 8-7-4-7 4 2-8-6-5 8-1z" fill="#fff" />
    </>
  ),
  invite: () => (
    <>
      <rect x="10" y="28" width="80" height="52" rx="7" fill="#f4efe6" />
      <path d="M10 32l40 28 40-28" fill="none" stroke="#a8741a" strokeWidth="4" strokeLinejoin="round" />
      <circle cx="74" cy="30" r="14" fill="#ff5a1f" />
      <path d="M74 23v14M67 30h14" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  xp: () => (
    <>
      <circle cx="50" cy="50" r="38" fill="#ffc83a" />
      <circle cx="50" cy="50" r="30" fill="none" stroke="#a8741a" strokeWidth="3" />
      <text x="50" y="62" textAnchor="middle" fontSize="34" fontWeight="800" fill="#a8741a" fontFamily="Barlow Condensed, sans-serif">XP</text>
    </>
  ),
};

const ITEM_RULES = [
  ['meals', /\bmeals?\b|\bmep\b|\bfood\b/i],
  ['giftCard', /gift card/i],
  ['gear', /gear credit/i],
  ['helmet', /helmet|\bsuit\b/i],
  ['dumbbell', /exercise gear|\bgear\b|weights?/i],
  ['ticket', /streak pass|\bpass\b/i],
  ['invite', /invite|\bcode\b/i],
  ['xp', /\bxp\b/i],
  ['title', /\btitle\b/i],
];

export const getItemArtKey = (text) => {
  const found = ITEM_RULES.find(([, pattern]) => pattern.test(String(text || '')));
  return found ? found[0] : null;
};

export const ItemArt = ({ text, size, fit = false }) => {
  const key = getItemArtKey(text);
  if (!key) {
    return <Box aria-hidden="true" sx={{ width: `${size}px`, fontSize: `${size * 0.8}px`, lineHeight: 1, textAlign: 'center' }}>🎁</Box>;
  }
  return (
    <Box
      component="svg"
      aria-hidden="true"
      viewBox={ART_VIEWBOX}
      sx={fit ? { flex: '1 1 0', minHeight: 0, maxHeight: `${size}px`, aspectRatio: '1 / 1', overflow: 'visible', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))', '@container (max-height: 250px)': { flex: 'none', width: '46cqh', height: '46cqh', maxHeight: 'none' } } : { flex: 'none', width: `${size}px`, height: `${size}px`, overflow: 'visible', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))' }}
    >
      {ITEM_ICONS[key]()}
    </Box>
  );
};
